
import { Link } from 'react-router-dom';
import {
  Package,
  Tag,
  AlertCircle,
  Sparkles,
  Trash2,
  Eye,
  EyeOff,
  ShoppingBag,
  ChevronRight,
  Bell,
} from 'lucide-react';
import { Notification, NotificationType } from '@bookstore/shared';
import { formatDistanceToNow } from 'date-fns';

interface NotificationCardProps {
  notification: Notification;
  isDeleting?: boolean;
  onToggleRead: (notification: Notification) => void;
  onDelete: (notificationId: string) => void;
  isMarkingAsRead?: boolean;
  isDeletingNotification?: boolean;
}

const NotificationCard = ({
  notification,
  isDeleting = false,
  onToggleRead,
  onDelete,
  isMarkingAsRead = false,
  isDeletingNotification = false,
}: NotificationCardProps) => {
  // Get icon based on notification type
  const getNotificationIcon = (type: NotificationType) => {
    const icons = {
      [NotificationType.ORDER]: Package,
      [NotificationType.PROMOTION]: Tag,
      [NotificationType.BOOK]: ShoppingBag,
      [NotificationType.SYSTEM]: AlertCircle,
      [NotificationType.GENERAL]: Sparkles,
    };
    return icons[type] || Bell;
  };

  // Get color based on notification type
  const getNotificationColor = (type: NotificationType) => {
    const colors = {
      [NotificationType.ORDER]:
        'bg-gradient-to-br from-blue-50 to-blue-100/50 text-blue-700 border-blue-200/50 shadow-blue-100/50',
      [NotificationType.PROMOTION]:
        'bg-gradient-to-br from-purple-50 to-purple-100/50 text-purple-700 border-purple-200/50 shadow-purple-100/50',
      [NotificationType.BOOK]:
        'bg-gradient-to-br from-green-50 to-green-100/50 text-green-700 border-green-200/50 shadow-green-100/50',
      [NotificationType.SYSTEM]:
        'bg-gradient-to-br from-orange-50 to-orange-100/50 text-orange-700 border-orange-200/50 shadow-orange-100/50',
      [NotificationType.GENERAL]:
        'bg-gradient-to-br from-pink-50 to-pink-100/50 text-pink-700 border-pink-200/50 shadow-pink-100/50',
    };
    return colors[type] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Get action button text
  const getActionButtonText = (type: NotificationType) => {
    const texts = {
      [NotificationType.ORDER]: 'View Order',
      [NotificationType.PROMOTION]: 'Shop Now',
      [NotificationType.BOOK]: 'Browse Books',
      [NotificationType.SYSTEM]: 'View Details',
      [NotificationType.GENERAL]: 'View Details',
    };
    return texts[type] || 'View';
  };

  const Icon = getNotificationIcon(notification.type);

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out
        ${isDeleting ? 'opacity-0 -translate-x-full scale-95' : 'opacity-100 translate-x-0 scale-100'}
      `}
    >
      <div
        className={`
          group relative overflow-hidden
          bg-background-soft border rounded-2xl
          shadow-sm hover:shadow-xl transition-all duration-300
          ${
            !notification.isRead
              ? 'border-primary-300/60 bg-gradient-to-br from-primary-50/40 via-background-soft to-background-soft ring-2 ring-primary-100/50'
              : 'border-border hover:border-primary-200/50'
          }
        `}
      >
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/0 via-transparent to-transparent group-hover:from-primary-500/5 transition-all duration-500 pointer-events-none"></div>

        {/* Unread Indicator - Enhanced */}
        {!notification.isRead && (
          <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-primary-600 via-primary-500 to-primary-400 rounded-r-full shadow-lg shadow-primary-500/30"></div>
        )}

        <div className="relative p-6">
          <div className="flex gap-4">
            {/* Icon - Enhanced with gradient */}
            <div className="flex-shrink-0">
              <div
                className={`
                  w-14 h-14 rounded-2xl
                  flex items-center justify-center border-2
                  shadow-lg transition-all duration-300
                  group-hover:scale-110 group-hover:rotate-3
                  ${getNotificationColor(notification.type)}
                `}
              >
                <Icon className="w-7 h-7" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Title & Time */}
              <div className="flex items-start justify-between gap-4 mb-2">
                <h4 className="font-bold text-text text-lg leading-tight group-hover:text-primary-700 transition-colors">
                  {notification.title}
                </h4>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs text-text-secondary/80 whitespace-nowrap font-medium">
                    {formatDistanceToNow(new Date(notification.createdAt.toDate()), {
                      addSuffix: true,
                    })}
                  </span>
                  {!notification.isRead && (
                    <span className="px-2 py-0.5 bg-primary-600 text-white text-[10px] font-bold rounded-full shadow-sm">
                      NEW
                    </span>
                  )}
                </div>
              </div>

              {/* Message */}
              <p className="text-sm text-text-secondary/90 mb-5 leading-relaxed">
                {notification.message}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Primary Action Button */}
                {notification.actionUrl && (
                  <Link
                    to={notification.actionUrl}
                    onClick={() => {
                      if (!notification.isRead) {
                        onToggleRead(notification);
                      }
                    }}
                    className="
                      inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                      bg-gradient-to-r from-primary-600 to-primary-700
                      text-white text-sm font-semibold
                      hover:from-primary-700 hover:to-primary-800
                      transition-all duration-300 hover:-translate-y-0.5
                      shadow-md shadow-primary-600/30 hover:shadow-lg hover:shadow-primary-600/40
                    "
                  >
                    {getActionButtonText(notification.type)}
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                )}

                {/* Mark Read/Unread */}
                <button
                  onClick={() => onToggleRead(notification)}
                  disabled={isMarkingAsRead}
                  className="
                    inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                    bg-background-subtle/80 text-text-secondary text-sm font-medium
                    hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200
                    transition-all duration-200
                    border border-border
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {notification.isRead ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      <span className="hidden sm:inline">Mark Unread</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Mark Read</span>
                    </>
                  )}
                </button>

                {/* Delete */}
                <button
                  onClick={() => onDelete(notification.id)}
                  disabled={isDeletingNotification}
                  className="
                    inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                    text-red-600 text-sm font-medium
                    hover:bg-red-50 hover:border-red-300
                    transition-all duration-200
                    border border-red-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;